'use client';
import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  ChangeEvent,
  KeyboardEvent,
} from 'react';
import { FieldHelperProps, useField, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import Tippy from '@tippyjs/react';

interface InputProps {
  type: string;
  label: string;
  name?: string;
  className?: string;
  IsDisabled?: boolean;
  hideErreur?: boolean;
  isShowPassword?: boolean;
  hasError?: boolean;
  active?: boolean;
  value?: string;
  numericOnly?: boolean;
  hasDoubleValue?: boolean;
  isHiddenItem?: boolean;
  hideErrorMessage?: boolean;
  tooltip?: string;
  autoComplete?: string;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
}

const Input: React.FC<InputProps> = ({
  type,
  name,
  label,
  className,
  IsDisabled,
  hideErreur = false,
  isShowPassword,
  hasError = false,
  active = false,
  value,
  numericOnly = false,
  isHiddenItem = false,
  hasDoubleValue = false,
  hideErrorMessage = false,
  tooltip,
  autoComplete,
  onChange,
}) => {
  const { t } = useTranslation();
  const [field, meta, helpers] = name
    ? useField(name)
    : [{}, {}, null as FieldHelperProps<any> | null];
  const [focused, setFocused] = useState(!field.value);
  const [labelWidth, setLabelWidth] = useState(0);
  const [showPassword, setshowPassword] = useState(isShowPassword);
  const [showHiddenItem, setshowHiddenItem] = useState(true);
  // const {setFieldTouched} = useFormikContext();

  const labelRef = useRef<HTMLLabelElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const label = labelRef.current;
    if (!label) return;
    if (label.offsetWidth > 0) {
      setLabelWidth(label.offsetWidth);
    }
    const observer = new ResizeObserver(() => {
      if (label.offsetWidth > 0) {
        setLabelWidth(label.offsetWidth);
      }
    });
    observer.observe(label);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (inputRef.current && value) {
      inputRef.current.value = value ?? '';
    }
  }, [inputRef.current]);

  useEffect(() => {
    setFocused((field.value != null && field.value !== '' && true) || value);
  }, [field.value]);

  const handleBlur = useCallback(() => {
    setFocused(inputRef.current?.value !== '');
  }, []);

  const handleFocus = useCallback(() => {
    setFocused(true);
  }, []);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (onChange) {
        onChange(e);
      }
      if (numericOnly) {
        if (hasDoubleValue) {
          const numericValue = e.target.value.replace(/[^0-9,.]/g, '');
          const parts = numericValue.split(/[,.]/);
          if (parts.length > 2) {
            const separator = numericValue.match(/[,.]/)?.[0] || '';
            helpers?.setValue?.(
              `${parts[0]}${separator}${parts.slice(1).join('')}`,
            );
          } else {
            helpers?.setValue?.(numericValue);
          }
        } else {
          const numericValue = e.target.value.replace(/[^0-9]/g, '');
          helpers?.setValue?.(numericValue);
        }
      } else {
        helpers?.setValue?.(e.target.value);
      }
    },
    [numericOnly, hasDoubleValue, helpers, onChange],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (numericOnly) {
        const key = e.key;
        if (hasDoubleValue) {
          // Allow digits, comma, dot, and navigation keys
          if (
            !/^[0-9,.]$/.test(key) &&
            key !== 'Backspace' &&
            key !== 'Delete' &&
            !key.startsWith('Arrow') &&
            key !== 'Home' &&
            key !== 'End' &&
            key !== 'Tab'
          ) {
            e.preventDefault();
          }
        } else {
          if (
            !/^[0-9]$/.test(key) &&
            key !== 'Backspace' &&
            key !== 'Delete' &&
            !key.startsWith('Arrow') &&
            key !== 'Home' &&
            key !== 'End' &&
            key !== 'Tab'
          ) {
            e.preventDefault();
          }
        }
      }
    },
    [numericOnly, hasDoubleValue],
  );

  const hasValue = () => {
    if (
      field.value !== undefined &&
      field.value !== null &&
      field.value !== ''
    ) {
      return true;
    }
    if (value !== undefined && value !== null && value !== '') {
      return true;
    }
    return false;
  };

  return (
    <>
      <div
        className={`minthr-form-outline ${hideErrorMessage ? 'minthr-mb-0' : ''} ${((meta.touched && meta.error) || hasError) && !hideErreur ? 'error' : ''} ${className || ''}`}
      >
        <input
          ref={inputRef}
          id={name}
          {...field}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          type={showPassword ? 'text' : type}
          onBlur={handleBlur}
          onFocus={handleFocus}
          autoComplete={autoComplete}
          className={`minthr-form-control ${focused || active ? 'active' : ''}`}
          disabled={IsDisabled}
        />
        {showHiddenItem && isHiddenItem && hasValue() && (
          <div
            className="div-sensible minthr-px-3 minthr-py-2"
            hidden={!showHiddenItem}
          >
            <p className="minthr-flex minthr-flex-items-center">*******</p>
            <p></p>
          </div>
        )}
        {type == 'password' && (
          <button
            className="show-password"
            type="button"
            onClick={() => {
              setshowPassword(!showPassword);
            }}
          >
            <img src="/auth-icons/eye.svg" alt="" />
          </button>
        )}
        {isHiddenItem && (
          <button
            className="show-password"
            type="button"
            onClick={() => {
              setshowHiddenItem(!showHiddenItem);
            }}
          >
            <img src="/auth-icons/eye.svg" alt="" />
          </button>
        )}
        {tooltip && (
          <div className="tooltip-container">
            <Tippy content={tooltip}>
              <i className="fas fa-exclamation-triangle custom-icon"></i>
            </Tippy>
          </div>
        )}
        <label ref={labelRef} className="minthr-form-label">
          {label}
        </label>
        <>
          {!hideErrorMessage && meta.touched && meta.error && !hideErreur ? (
            <div className="minthr-form-message">
              <span className="field-validation-error">
                <span>{t(meta.error)}</span>
              </span>
            </div>
          ) : null}
        </>
        <div className="minthr-form-notch">
          <div className="minthr-form-notch-leading"></div>
          <div
            className="minthr-form-notch-middle"
            style={{ minWidth: `${labelWidth + 20}px` }}
          ></div>
          <div className="minthr-form-notch-trailing"></div>
        </div>
      </div>
    </>
  );
};

export default Input;
